import { useState } from 'react';

import type { MaintenanceListTableGroupItem } from '@/components/sections/service/maintenance-list/table';
import {
  StyledGridLabelValue,
  StyledRowItemsWrapper,
  StyledTd,
} from '@/components/sections/service/maintenance-list/table/full';
import { StyledScrollSnapWrapper } from '@/components/sections/service/maintenance-list/table/full/scrollbar';
import { ButtonIcon } from '@/components/shared/buttons/button-icon';
import { Icon } from '@/components/shared/element/icons';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Flex } from '@/components/shared/layout/flex';
import type { IVehicleType } from '@/types/services/contentstack';
import type { RegularMaintenaceSchedule } from '@/types/services/contentstack/services/maintenance-service';
import { IIcon } from '@/types/shared';

interface MaintenanceListTableFullTbodyProps {
  generalTitle: string;
  generalIcon?: IIcon;
  tableItems: MaintenanceListTableGroupItem[];
  vehicleType: IVehicleType;
  maintenanceSchedules: RegularMaintenaceSchedule[];
  selectedIndex?: number;
}

export const MaintenanceListTableFullTbody = (
  props: MaintenanceListTableFullTbodyProps
) => {
  const [isOpenIndexes, setIsOpenIndexes] = useState<string>('');

  const onToggleFullTableRowGroupByIndex = (index: number) => {
    const indexes = isOpenIndexes.split(',');
    const find = indexes.indexOf(`${index}`);
    if (find > -1) {
      indexes.splice(find, 1);
    } else {
      indexes.push(`${index}`);
    }
    setIsOpenIndexes(indexes.join());
  };
  return (
    <Box
      test={{
        dataTest: 'maintenance_tableBody',
      }}
    >
      {props.vehicleType === 'lcv' && (
        <StyledGridLabelValue isGroupTitleRow isBodyRow>
          <StyledTd isGroupTitle isLabel>
            <Flex alignItems="center" columnGap="2">
              <Icon icon={props.generalIcon} size="sm" />
              <Text as="p" font="bold">
                {props.generalTitle}
              </Text>
            </Flex>
          </StyledTd>
          <StyledScrollSnapWrapper>
            <StyledRowItemsWrapper className="scrollable-area">
              {props.maintenanceSchedules.map((schedule, index) => (
                <StyledTd
                  key={`general_${schedule.month}`}
                  isGroupTitle
                  isTableValueCell
                  align="center"
                  isActive={index === props.selectedIndex}
                >
                  <Icon icon="check-circle" color="icon-green" size="sm" />
                </StyledTd>
              ))}
            </StyledRowItemsWrapper>
          </StyledScrollSnapWrapper>
        </StyledGridLabelValue>
      )}

      {props.tableItems.map((group, groupIndex) => (
        <Box key={`${group.key}${groupIndex}`}>
          <StyledGridLabelValue isGroupTitleRow={group.isGroup} isBodyRow>
            <StyledTd
              isGroupTitle
              isLabel
              css={{
                cursor: 'pointer',
              }}
              onClick={() => onToggleFullTableRowGroupByIndex(groupIndex)}
            >
              <Flex alignItems="center" columnGap="2">
                <ButtonIcon
                  icon="expand-more"
                  size="xs"
                  variant="button-icon-color-blue"
                />
                <Text as="p" font="bold">
                  {group.labelTitle}
                </Text>
              </Flex>
            </StyledTd>
            <StyledScrollSnapWrapper>
              <StyledRowItemsWrapper className="scrollable-area">
                {group.values?.map((value, index) => (
                  <StyledTd
                    key={`${group.key}${index}Result`}
                    isGroupTitle
                    isTableValueCell
                    align="center"
                    isActive={index === props.selectedIndex}
                    css={{
                      display:
                        index !== props.selectedIndex &&
                        isOpenIndexes.split(',').includes(`${groupIndex}`)
                          ? 0
                          : 1,
                    }}
                  >
                    <Icon
                      icon={value ? 'check-circle' : 'cancel'}
                      color={value ? 'icon-green' : 'icon-gray-lightest'}
                      size="sm"
                      css={{
                        display: isOpenIndexes
                          .split(',')
                          .includes(`${groupIndex}`)
                          ? 'none'
                          : 'block',
                      }}
                    />
                  </StyledTd>
                ))}
              </StyledRowItemsWrapper>
            </StyledScrollSnapWrapper>
          </StyledGridLabelValue>
          <Box
            display={
              isOpenIndexes.split(',').includes(`${groupIndex}`)
                ? 'block'
                : 'none'
            }
          >
            {group.children?.map((child, childIndex) => (
              <StyledGridLabelValue
                key={`${group.key}${child.key}${childIndex}`}
                isBodyRow
                css={{
                  bc: '$gray200',
                  '&:nth-child(even)': {
                    bc: '$white',
                  },
                }}
              >
                <StyledTd isLabel>
                  <Flex alignItems="center" columnGap="2">
                    <Text as="p" css={{ pl: '$space-8' }}>
                      {child.labelTitle}
                    </Text>
                  </Flex>
                </StyledTd>
                <StyledScrollSnapWrapper>
                  {child.valueText ? (
                    <StyledTd isTableTextLineCell align="center">
                      <Text>{child.valueText}</Text>
                    </StyledTd>
                  ) : (
                    <StyledRowItemsWrapper className="scrollable-area">
                      {child.values?.map((value, index) => (
                        <StyledTd
                          key={`${group.key}${child.key}${index}Result`}
                          isTableValueCell
                          align="center"
                          isActive={index === props.selectedIndex}
                        >
                          <Icon
                            icon={value ? 'check-circle' : 'cancel'}
                            color={value ? 'icon-green' : 'icon-gray-lightest'}
                            size="sm"
                          />
                        </StyledTd>
                      ))}
                    </StyledRowItemsWrapper>
                  )}
                </StyledScrollSnapWrapper>
              </StyledGridLabelValue>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

MaintenanceListTableFullTbody.displayName =
  'SectionMaintenanceListTableFullTbody';
